/**
 * Auth API Calls
 * CCH Care - Version 1.2
 *
 * Changes (2026-04-21) — Password reset (Feature #11):
 * - Added requestPasswordReset(), validateResetToken(), resetPassword()
 *   for the forgot/reset password flow. Backend endpoints at
 *   /auth/forgot-password.php and /auth/reset-password.php.
 *
 * Changes (2026-04-19) — Remember Me:
 * - login() and verify2FA() accept an optional `rememberMe` boolean
 *   and forward it to the backend as `remember_me`. Default false
 *   preserves existing call sites.
 */
import client from './client';

/**
 * Login — Step 1: Email + password
 * Returns either { requires_2fa } or { requires_2fa_setup }
 *
 * @param {string} email
 * @param {string} password
 * @param {boolean} [rememberMe=false]  Sent to backend; login.php bakes
 *   this into the temp token so verify-2fa.php issues a session JWT
 *   with the appropriate expiry (8h default, 30d when remembered).
 */
export async function login(email, password, rememberMe = false) {
  const response = await client.post('/auth/login.php', {
    email,
    password,
    remember_me: rememberMe,
  });
  return response.data;
}

/**
 * Verify 2FA — Step 2: TOTP code
 * Returns session token + user data
 *
 * @param {string} tempToken
 * @param {string} code
 * @param {boolean} [rememberMe=false]  Belt-and-suspenders — the
 *   authoritative flag comes from the signed temp token payload;
 *   passing it here keeps body and token in agreement.
 */
export async function verify2FA(tempToken, code, rememberMe = false) {
  const response = await client.post('/auth/verify-2fa.php', {
    temp_token: tempToken,
    code,
    remember_me: rememberMe,
  });
  return response.data;
}

/**
 * Validate session — called on app load and route changes
 */
export async function validateSession() {
  const response = await client.get('/auth/session.php');
  return response.data;
}

/**
 * Logout
 */
export async function logout() {
  try {
    await client.post('/auth/logout.php');
  } catch (e) {
    // Logout should always succeed client-side even if API call fails
  }
}

/**
 * Register new organization (self-service trial)
 */
export async function register(data) {
  const response = await client.post('/auth/register.php', data);
  return response.data;
}

/**
 * Get trial status
 */
export async function getTrialStatus() {
  const response = await client.get('/auth/trial-status.php');
  return response.data;
}

/**
 * Request full activation
 */
export async function requestActivation(planRequested, notes) {
  const response = await client.post('/auth/request-activation.php', {
    plan_requested: planRequested,
    notes,
  });
  return response.data;
}

/**
 * Request a password reset email.
 *
 * Backend intentionally returns the same generic success message whether
 * the email exists or not (enumeration prevention). UI should show the
 * same confirmation screen regardless.
 *
 * Rate limited to 3 requests/hour/email server-side.
 *
 * @param {string} email
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export async function requestPasswordReset(email) {
  const response = await client.post('/auth/forgot-password.php', { email });
  return response.data;
}

/**
 * Validate a password reset token (GET).
 * Called on ResetPasswordPage mount to check link validity before
 * showing the new-password form.
 *
 * @param {string} token
 * @returns {Promise<{ success: boolean, valid: boolean, email: string, expires_in_minutes: number }>}
 *   `email` is masked (e.g. "jo****@example.com"). Errors (invalid/expired
 *   token) come back with success: false and a user-facing message.
 */
export async function validateResetToken(token) {
  const response = await client.get('/auth/reset-password.php', {
    params: { token },
  });
  return response.data;
}

/**
 * Reset password using a validated token (POST).
 *
 * @param {string} token
 * @param {string} password
 * @returns {Promise<{ success: boolean, message: string }>}
 *   On success, the user should be redirected to /login. The backend
 *   does NOT mint a session — user logs in fresh with their new password.
 */
export async function resetPassword(token, password) {
  const response = await client.post('/auth/reset-password.php', {
    token,
    password,
  });
  return response.data;
}